import { Frame, Bookmark } from 'lucide-react';
import { DiscoveryCard } from './discovery-card';
import { ImportanceBadge } from '@/components/ui/importance-badge';

interface SeamOrFrame {
    startRef: string;
    endRef: string;
    device: 'Inclusio' | 'Frame';
    note?: string;
    importance?: 'high' | 'medium' | 'low';
}

interface SeamsAndFramesCardProps {
    seams: SeamOrFrame[];
    isExpanded: boolean;
    onToggleExpansion: () => void;
    onRefClick?: (ref: string) => void;
    thumbnailUrl?: string;
}

export function SeamsAndFramesCard({ seams, isExpanded, onToggleExpansion, onRefClick, thumbnailUrl }: SeamsAndFramesCardProps) {
    if (!seams || seams.length === 0) return null;

    return (
        <DiscoveryCard
            isExpanded={isExpanded}
            onToggle={onToggleExpansion}
            icon={Frame}
            thumbnailUrl={thumbnailUrl}
            header={
                <div className="flex flex-col gap-1">
                    <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-primary/50">Structural Boundaries</div>
                    <h3 className="text-xl font-serif font-medium text-foreground/90 leading-tight">Macro Seams & Inclusios</h3>
                </div>
            }
        >
            <div className="pt-4 space-y-4">
                <p className="text-xs text-muted-foreground italic mb-2">
                    Boundary markers that bracket sections of the book, often repeating key themes at the beginning and end.
                </p>

                <div className="grid gap-3">
                    {seams.map((seam, idx) => (
                        <div key={idx} className="flex flex-col p-4 rounded-xl border border-border/20 bg-background/20">
                            <div className="flex items-center justify-between gap-4 mb-2">
                                <div className="flex items-center gap-2">
                                    <div className="p-1 rounded bg-foreground/5">
                                        <Bookmark className="h-3 w-3 text-muted-foreground/50" />
                                    </div>
                                    <div className="flex items-center gap-1.5 text-sm font-bold font-serif">
                                        <span className="cursor-pointer hover:text-primary transition-colors" onClick={() => onRefClick?.(seam.startRef)}>{seam.startRef}</span>
                                        <span className="text-muted-foreground/30 font-normal">thru</span>
                                        <span className="cursor-pointer hover:text-primary transition-colors" onClick={() => onRefClick?.(seam.endRef)}>{seam.endRef}</span>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    {seam.importance && <ImportanceBadge importance={seam.importance} />}
                                    <span className="text-[10px] font-bold uppercase tracking-widest text-primary/40 px-2 py-0.5 rounded border border-primary/10">
                                        {seam.device}
                                    </span>
                                </div>
                            </div>
                            {seam.note && (
                                <p className="text-xs text-foreground/70 leading-relaxed font-light mt-1 pl-4 border-l border-primary/10">
                                    {seam.note}
                                </p>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        </DiscoveryCard>
    );
}
